import { Line, LineChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { MultiSeriesComparisonChart, WaveformChart } from '../respiratory-muscle/RespiratoryMuscleCharts';
import AssessmentHistoryComparisonCard, {
  type AssessmentHistoryComparisonEntry,
} from '../components/AssessmentHistoryComparisonCard';
import {
  buildBlockComparisonData,
  buildEnduranceInterpretation,
  buildWavePoints,
  getRespiratoryEnduranceProtocolLines,
  getRespiratoryEnduranceModalityLabel,
  getRespiratorySeverityTone,
  summarizeRespiratoryEndurancePayload,
  type RespiratoryEnduranceResultPayload,
} from '../respiratory-muscle/respiratoryMuscleDomain';
import { resolveAssessmentInterpretationText } from '../utils/interpretationOverride';

export const getRespiratoryEnduranceSeverityLabel = (payload?: RespiratoryEnduranceResultPayload | null): string =>
  buildEnduranceInterpretation(payload).title;

export const getRespiratoryEnduranceSeverityTone = (payload?: RespiratoryEnduranceResultPayload | null): string =>
  getRespiratorySeverityTone(buildEnduranceInterpretation(payload).tone);

export const getRespiratoryEnduranceInterpretationText = (payload?: RespiratoryEnduranceResultPayload | null): string => {
  const summary = summarizeRespiratoryEndurancePayload(payload);
  const interpretation = buildEnduranceInterpretation(payload);
  return resolveAssessmentInterpretationText(
    payload,
    `${interpretation.title}${summary.workReduction != null ? `, com redução de trabalho de ${summary.workReduction.toFixed(1)}%.` : '.'}`,
  );
};

interface RespiratoryEnduranceResultRendererProps {
  payload: RespiratoryEnduranceResultPayload | null | undefined;
  chartImages?: Record<string, string> | null;
  assessmentDate?: string | null;
  comparisonHistory?: AssessmentHistoryComparisonEntry[];
}

export default function RespiratoryEnduranceResultRenderer({
  payload,
  chartImages = null,
  assessmentDate = null,
  comparisonHistory = [],
}: RespiratoryEnduranceResultRendererProps) {
  const summary = summarizeRespiratoryEndurancePayload(payload);
  const interpretation = buildEnduranceInterpretation(payload);
  const blocks = Array.isArray(payload?.blocks) ? payload!.blocks : [];
  const plotBreaths = Array.isArray(payload?.plotBreaths) ? payload!.plotBreaths : [];
  const blockComparison = buildBlockComparisonData(plotBreaths, blocks);
  const firstWaveform = plotBreaths[0] ?? null;
  const blockChartData = blocks.map((block) => ({
    block: block.index,
    flow: block.meanPeakFlow,
    volume: block.meanPeakVolume,
    work: block.meanTotalWork,
  }));
  const protocolLines = getRespiratoryEnduranceProtocolLines(payload?.protocol);
  const optionalMetrics = [
    summary.targetResistance != null ? { label: 'Carga alvo', value: `${summary.targetResistance.toFixed(1)} cmH2O` } : null,
    summary.flowReduction != null ? { label: 'Redução do fluxo', value: `${summary.flowReduction.toFixed(1)}%` } : null,
    summary.volumeReduction != null ? { label: 'Redução do volume', value: `${summary.volumeReduction.toFixed(1)}%` } : null,
    summary.workReduction != null ? { label: 'Redução do trabalho', value: `${summary.workReduction.toFixed(1)}%` } : null,
  ].filter((item): item is { label: string; value: string } => item != null);

  return (
    <div className="space-y-6">
      <div className="grid gap-6 md:grid-cols-5">
        <div className="rounded-[28px] border border-slate-200 bg-white p-8 text-center shadow-sm md:col-span-2">
          <p className="mb-4 text-xs font-black uppercase tracking-[0.25em] text-slate-600">ENDURANCE RESPIRATÓRIO</p>
          <div className="text-6xl font-black leading-none text-slate-900">{summary.tlimFormatted ?? '-'}</div>
          <div className="mt-2 text-sm font-semibold text-slate-700">Tlim</div>
          <div className={`mt-4 inline-flex rounded-full px-4 py-1.5 text-xs font-bold shadow-sm ${getRespiratoryEnduranceSeverityTone(payload)}`}>{interpretation.title}</div>
        </div>

        <div className="flex flex-col justify-center space-y-4 md:col-span-3">
          <div>
            <h4 className="text-2xl font-black tracking-tight text-slate-800">Interpretação</h4>
            <p className="mt-3 max-w-3xl text-sm leading-relaxed text-slate-600">{getRespiratoryEnduranceInterpretationText(payload)}</p>
            <ul className="mt-3 space-y-1 text-xs leading-relaxed text-slate-500">
              {interpretation.lines.filter((line) => !(summary.workReduction != null && line.startsWith('Redução do trabalho'))).map((line, index) => <li key={`endurance-summary-line-${index}`}>{line}</li>)}
            </ul>
          </div>
          <div className="grid gap-3 text-sm text-slate-600 md:grid-cols-2">
            <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
              Origem dos dados: <span className="font-semibold text-slate-900">{payload?.protocol?.entryMode === 'manual' ? 'registro manual' : 'BreatheLink CSV'}</span>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
              Modalidade: <span className="font-semibold text-slate-900">{payload?.protocol ? getRespiratoryEnduranceModalityLabel(payload.protocol.modality) : 'não informada'}</span>
            </div>
          </div>
        </div>
      </div>

      <AssessmentHistoryComparisonCard
        testKey="RESP_ENDURANCE"
        currentPayload={payload}
        currentAssessmentDate={assessmentDate}
        history={comparisonHistory}
      />

      {protocolLines.length > 1 && (
        <div className="rounded-[24px] border border-slate-200 bg-slate-50 p-5">
          <div className="text-[10px] font-black uppercase tracking-[0.22em] text-slate-400">Protocolo aplicado</div>
          <div className="mt-3 grid gap-2 text-sm text-slate-700 md:grid-cols-2">{protocolLines.slice(1).map((line, index) => <div key={`endurance-protocol-${index}`}>{line}</div>)}</div>
        </div>
      )}

      {optionalMetrics.length > 0 && (
        <div className={`grid gap-6 ${optionalMetrics.length >= 3 ? 'md:grid-cols-3' : 'md:grid-cols-2'}`}>
          {optionalMetrics.map((item) => <Metric key={item.label} label={item.label} value={item.value} />)}
        </div>
      )}

      {chartImages?.comparison_pressure ? (
        <div className="grid gap-6 md:grid-cols-2">
          {[
            ['comparison_pressure', 'Pressão'],
            ['comparison_flow', 'Fluxo'],
            ['comparison_volume', 'Volume'],
            ['comparison_work', 'Trabalho'],
          ].map(([key, label]) => (
            chartImages[key] ? (
              <div key={key} className="rounded-[24px] border border-slate-100 bg-white p-6 shadow-sm">
                <div className="text-[10px] font-black uppercase tracking-[0.24em] text-slate-400">{label}</div>
                <img src={chartImages[key]} alt={`Gráfico ${label}`} className="mt-4 w-full rounded-2xl border border-slate-100" />
              </div>
            ) : null
          ))}
        </div>
      ) : blockComparison ? (
        <div className="grid gap-6 md:grid-cols-2">
          <MultiSeriesComparisonChart title="Pressão (cmH2O)" unit="cmH2O" data={blockComparison.pressure} seriesMeta={blockComparison.seriesMeta} />
          <MultiSeriesComparisonChart title="Fluxo (L/s)" unit="L/s" data={blockComparison.flow} seriesMeta={blockComparison.seriesMeta} />
          <MultiSeriesComparisonChart title="Volume (L)" unit="L" data={blockComparison.volume} seriesMeta={blockComparison.seriesMeta} />
          <MultiSeriesComparisonChart title="Trabalho acumulado" unit="cmH2O.L" data={blockComparison.work} seriesMeta={blockComparison.seriesMeta} />
        </div>
      ) : null}

      {firstWaveform && !blockComparison ? (
        <div className="grid gap-6 md:grid-cols-2">
          <WaveformChart title="Pressão (cmH2O)" color="#0f172a" unit="cmH2O" dataKey="pressure" data={buildWavePoints(firstWaveform)} />
          <WaveformChart title="Fluxo (L/s)" color="#059669" unit="L/s" dataKey="flow" data={buildWavePoints(firstWaveform)} />
        </div>
      ) : null}

      {chartImages?.block_evolution ? (
        <div className="rounded-[24px] border border-slate-100 bg-white p-6 shadow-sm">
          <div className="text-[10px] font-black uppercase tracking-[0.24em] text-slate-400">Trabalho respiratório ao longo do teste</div>
          <img src={chartImages.block_evolution} alt="Trabalho respiratório ao longo do teste" className="mt-4 w-full rounded-2xl border border-slate-100" />
        </div>
      ) : blockChartData.length > 1 ? (
        <div className="rounded-[24px] border border-slate-100 bg-white p-6 shadow-sm">
          <div className="text-[10px] font-black uppercase tracking-[0.24em] text-slate-400">Trabalho respiratório ao longo do teste</div>
          <div className="mt-4 h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={blockChartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="block" tickFormatter={(value) => `#${value}`} />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="work" stroke="#f97316" name="Trabalho" dot={false} strokeWidth={2} isAnimationActive={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      ) : null}

      {blocks.length ? (
        <div className="overflow-x-auto rounded-[24px] border border-slate-100 bg-white shadow-sm">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-slate-500">
              <tr>
                <th className="px-4 py-3 text-left">Bloco</th>
                <th className="px-4 py-3 text-left">Respirações</th>
                <th className="px-4 py-3 text-left">Pico fluxo</th>
                <th className="px-4 py-3 text-left">Pico volume</th>
                <th className="px-4 py-3 text-left">Trabalho</th>
              </tr>
            </thead>
            <tbody>
              {blocks.map((block) => (
                <tr key={block.index} className="border-t">
                  <td className="px-4 py-3">Bloco {block.index}</td>
                  <td className="px-4 py-3">{block.breathRange}</td>
                  <td className="px-4 py-3">{block.meanPeakFlow.toFixed(2)} L/s</td>
                  <td className="px-4 py-3">{block.meanPeakVolume.toFixed(2)} L</td>
                  <td className="px-4 py-3">{block.meanTotalWork.toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
}

const Metric = ({ label, value }: { label: string; value: string }) => (
  <div className="rounded-[24px] border border-slate-100 bg-white p-5 shadow-sm">
    <div className="text-[10px] font-black uppercase tracking-[0.22em] text-slate-400">{label}</div>
    <div className="mt-2 text-3xl font-black text-slate-900">{value}</div>
  </div>
);
